import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, MessageCircle, Clock, ArrowRight, CheckCircle2, Linkedin } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/motion";
import useMeta from "../lib/useMeta";
import { breadcrumb } from "../lib/seo";
import { CONTACT, RESPONSE_PROMISE, LINKEDIN_URL, waLink } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  useMeta({
    title: "Contact Saarthva | Procurement & Operations Support",
    description: "Talk to Saarthva about procurement, sourcing, workplace supplies, managed procurement, operations support or new-site requirements.",
    path: "/contact",
    schemas: [breadcrumb("Contact", "/contact")],
  });
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, {
        name: form.name,
        company: form.company,
        phone: form.phone,
        email: form.email,
        requirement: "General Enquiry",
        details: form.message,
        contact_method: "Email",
      });
      setSent(true);
      toast.success("Message sent — we will reach out shortly.");
    } catch {
      toast.error("Could not send right now. Please try WhatsApp or call us directly.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-line bg-white px-4 py-3 text-sm text-navy placeholder:text-slate-400 outline-none transition-colors focus:border-steel focus:ring-2 focus:ring-steel/15";

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="CONTACT"
        title="Let's talk about"
        accent="your requirement."
        description={`Call, WhatsApp or write to us — ${RESPONSE_PROMISE.toLowerCase()}`}
      />

      <section className="bg-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2 space-y-5">
            <Reveal>
              <a href={`tel:+${CONTACT.phoneIntl}`} data-testid="contact-call-card" className="group flex items-start gap-4 rounded-2xl border border-line p-6 transition-all hover:border-saffron/50 hover:bg-cream/50">
                <span className="w-11 h-11 rounded-xl bg-cream grid place-items-center shrink-0 group-hover:bg-saffron transition-colors">
                  <Phone className="w-5 h-5 text-saffron group-hover:text-white transition-colors" strokeWidth={1.8} />
                </span>
                <span>
                  <span className="block font-display font-bold text-navy">Call us</span>
                  <span className="block text-sm text-slate-600 mt-1">{CONTACT.phoneDisplay}</span>
                </span>
              </a>
            </Reveal>
            <Reveal delay={0.08}>
              <a href={waLink("Hi Saarthva, I'd like to discuss a requirement.")} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-card" className="group flex items-start gap-4 rounded-2xl border border-line p-6 transition-all hover:border-saffron/50 hover:bg-cream/50">
                <span className="w-11 h-11 rounded-xl bg-cream grid place-items-center shrink-0 group-hover:bg-saffron transition-colors">
                  <MessageCircle className="w-5 h-5 text-saffron group-hover:text-white transition-colors" strokeWidth={1.8} />
                </span>
                <span>
                  <span className="block font-display font-bold text-navy">WhatsApp</span>
                  <span className="block text-sm text-slate-600 mt-1">Fastest way to reach us — usually instant</span>
                </span>
              </a>
            </Reveal>
            <Reveal delay={0.16}>
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-card" className="group flex items-start gap-4 rounded-2xl border border-line p-6 transition-all hover:border-saffron/50 hover:bg-cream/50">
                <span className="w-11 h-11 rounded-xl bg-cream grid place-items-center shrink-0 group-hover:bg-saffron transition-colors">
                  <Mail className="w-5 h-5 text-saffron group-hover:text-white transition-colors" strokeWidth={1.8} />
                </span>
                <span>
                  <span className="block font-display font-bold text-navy">Email</span>
                  <span className="block text-sm text-slate-600 mt-1">{CONTACT.email}</span>
                </span>
              </a>
            </Reveal>
            <Reveal delay={0.2}>
              <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin-card" className="group flex items-start gap-4 rounded-2xl border border-line p-6 transition-all hover:border-saffron/50 hover:bg-cream/50">
                <span className="w-11 h-11 rounded-xl bg-cream grid place-items-center shrink-0 group-hover:bg-saffron transition-colors">
                  <Linkedin className="w-5 h-5 text-saffron group-hover:text-white transition-colors" strokeWidth={1.8} />
                </span>
                <span>
                  <span className="block font-display font-bold text-navy">LinkedIn</span>
                  <span className="block text-sm text-slate-600 mt-1">Follow Saarthva for updates and sourcing insights</span>
                </span>
              </a>
            </Reveal>
            <Reveal delay={0.24}>
              <div className="flex items-start gap-4 rounded-2xl bg-navy text-white p-6" data-testid="contact-hours-card">
                <Clock className="w-5 h-5 text-saffron shrink-0 mt-0.5" strokeWidth={1.8} />
                <div>
                  <p className="font-display font-bold">{CONTACT.hours}</p>
                  <p className="text-sm text-slate-400 mt-1">{CONTACT.base}</p>
                  <Link to="/request-quote" data-testid="contact-quote-link" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-saffron hover:text-white transition-colors">
                    Have a detailed requirement? Request a Quote <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="lg:col-span-3">
            <div className="rounded-3xl border border-line bg-mist p-7 sm:p-10" data-testid="contact-form-card">
              {sent ? (
                <div className="text-center py-14" data-testid="contact-success">
                  <CheckCircle2 className="w-14 h-14 text-saffron mx-auto mb-5" strokeWidth={1.5} />
                  <h2 className="font-display text-2xl font-bold text-navy">Message received.</h2>
                  <p className="mt-2 text-slate-600">{RESPONSE_PROMISE}</p>
                  <button onClick={() => { setSent(false); setForm({ name: "", company: "", phone: "", email: "", message: "" }); }} data-testid="contact-send-another-button" className="mt-6 text-sm font-semibold text-steel hover:text-navy transition-colors">
                    Send another message
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="font-display text-2xl font-bold text-navy">Send a quick message</h2>
                  <p className="mt-1 text-sm text-slate-600 mb-7">For detailed requirements with quantities, use the full quote form.</p>
                  <form onSubmit={submit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-navy mb-1.5">Name *</label>
                        <input required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} data-testid="contact-input-name" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-navy mb-1.5">Company</label>
                        <input value={form.company} onChange={set("company")} placeholder="Company name" className={inputCls} data-testid="contact-input-company" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-navy mb-1.5">Phone *</label>
                        <input required type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputCls} data-testid="contact-input-phone" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-navy mb-1.5">Work Email *</label>
                        <input required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} data-testid="contact-input-email" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-navy mb-1.5">Message *</label>
                      <textarea required rows={4} value={form.message} onChange={set("message")} placeholder="Tell us briefly what you need..." className={inputCls} data-testid="contact-input-message" />
                    </div>
                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="contact-submit-button"
                      className="inline-flex items-center gap-2 rounded-full bg-saffron hover:bg-saffrondark disabled:opacity-60 text-white font-semibold px-8 py-3.5 transition-colors"
                    >
                      {sending ? "Sending..." : "Send Message"} <ArrowRight className="w-4 h-4" />
                    </button>
                  </form>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
